/**
 * Jeopardy's content. A category is its own item, not a string on each clue, so empty
 * columns, duplicate names, column order and empty rounds all survive; a clue names it by id.
 * `JeopardyClueBody` is what a pack stores; `JeopardyContent` is what `resolve` builds.
 */
import { z } from 'zod';
/** What a round is called before anyone renames it. */
export function defaultRoundName(round) {
    return `Round ${round}`;
}
/** Longest a round name may be: descriptive enough to read, short enough to fit a tab. */
export const MAX_ROUND_NAME = 40;
/** Longest a custom tile face may be: it still has to shrink to fit a single tile. */
export const MAX_TILE_TEXT = 48;
// ---------------------------------------------------------------------------
// What a pack stores
// ---------------------------------------------------------------------------
/** A column. `round` lives here because which rounds exist is derived from categories. */
export const categoryBodySchema = z.object({
    /** May be empty: hosts lay out a board before naming it. */
    name: z.string(),
    round: z.number().int().positive(),
    /** Left-to-right order on the board. */
    position: z.number().int().nonnegative(),
});
/** `content_items.schema_version` for a clue; v1 bodies are carried forward by `migrateClueBody`. */
export const JEOPARDY_CONTENT_VERSION = 2;
/**
 * A blank `text` is kept in storage so re-opening the editor keeps the slot;
 * `requests.ts` filters blanks out of the player-facing options.
 */
export const clueChoiceSchema = z.object({
    id: z.string().min(1),
    text: z.string(),
});
/**
 *  - `static`: a fixed face value.
 *  - `wager`: the stake is drawn from the round's pool at play time.
 *  - `accrued`: the player bets their own score, up to `maxWager`.
 */
export const clueScoringSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('static'), points: z.number().int().positive() }),
    z.object({ kind: z.literal('wager') }),
    z.object({ kind: z.literal('accrued'), maxWager: z.number().int().positive() }),
    /**
     * Final round: each player wagers up to their own score (`JeopardyState.finalWagers`),
     * then everyone writes and the host grades.
     */
    z.object({ kind: z.literal('final') }),
]);
/**
 * A bonus for answering a collect clue quickly (issue #11). Applied on top of the base award,
 * to correct answers only, from the clue's reveal to each answer's (last) submission.
 *
 *  - `static`: a flat bonus. `fastest` pays only the earliest correct answer(s); `before`
 *    pays every correct answer within `beforeSeconds` of the reveal.
 *  - `decay`: the bonus falls linearly from `maxPoints` at the reveal toward `minPoints` as
 *    the `seconds` window runs; past the window there is no bonus (§11).
 */
export const speedBonusSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('static'),
        points: z.number().int().positive(),
        scope: z.enum(['fastest', 'before']),
        beforeSeconds: z.number().int().positive().optional(),
    }),
    z.object({
        kind: z.literal('decay'),
        maxPoints: z.number().int().nonnegative(),
        minPoints: z.number().int().nonnegative(),
        seconds: z.number().int().positive(),
    }),
]);
/** What the board tile shows: the points (default), or free text. */
export const tileDisplaySchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('points') }),
    // Truncated, not rejected: a stored or imported body with a longer face is clipped to the
    // cap rather than dropping the whole clue from the board (`resolve` skips a clue that fails).
    z.object({ kind: z.literal('custom'), text: z.string().transform((t) => t.slice(0, MAX_TILE_TEXT)) }),
]);
/** An empty `prompt` or answer is a draft, not an error; `isDraftClue` is the one definition. */
const clueBaseShape = {
    categoryId: z.string().min(1),
    prompt: z.string(),
    /** Position within the column, so a board renders in a stable order. */
    position: z.number().int().nonnegative(),
    scoring: clueScoringSchema,
    /** Deducted on a wrong answer instead of zero. Not allowed with `wager` scoring. */
    negativePoints: z.number().int().nonnegative().optional(),
    /** The tile face: points, or a free string. */
    tileDisplay: tileDisplaySchema,
    /**
     * Default lockout; the host may flip it before revealing. Only verbal clues use it, but
     * every type keeps it so changing the type does not lose it.
     */
    lockout: z.enum(['host_enable', 'auto_unlock']),
    /**
     * Seconds on the clock. Null or 0 means untimed, and an untimed answer closes only by
     * hand: the reducer sets a deadline on the timed case alone.
     */
    answerSeconds: z.number().int().nonnegative().nullable(),
    /**
     * Bonus for a quick answer, collect clues only. Absent means none. Kept across type
     * changes like `lockout`; `reduce.ts` applies it only where everyone answers at once.
     */
    speedBonus: speedBonusSchema.optional(),
};
/**
 * A clue whose `categoryId` names nothing is dropped by `resolve`, not rejected: the schema
 * sees one body, not the pack, and an orphan should not stop a board rendering.
 */
export const clueBodySchema = z
    .discriminatedUnion('questionType', [
    z.object({ questionType: z.literal('verbal'), correctAnswer: z.string(), ...clueBaseShape }),
    z.object({
        questionType: z.literal('textInput'),
        correctAnswer: z.string(),
        /**
         * Additional accepted spellings. Its *presence* (even empty) opts the clue into
         * server-side auto-grading against the normalized correct + accepted set; absent keeps
         * the clue host-graded, so existing hosted boards are unchanged. Final clues ignore it.
         */
        acceptedAnswers: z.array(z.string()).optional(),
        ...clueBaseShape,
    }),
    z.object({
        questionType: z.literal('numericInput'),
        correctAnswer: z.string(),
        /** Absolute tolerance for an auto-graded numeric answer; absent means an exact match. */
        tolerance: z.number().nonnegative().optional(),
        ...clueBaseShape,
    }),
    z.object({
        questionType: z.literal('multipleChoice'),
        choices: z.array(clueChoiceSchema),
        // Exactly one right answer, enforced here rather than by the UI.
        correctChoiceIds: z.array(z.string()).length(1),
        ...clueBaseShape,
    }),
    z.object({
        questionType: z.literal('multipleAnswer'),
        choices: z.array(clueChoiceSchema),
        correctChoiceIds: z.array(z.string()).min(1),
        /** Partial credit for a partly-right pick. */
        partialScoring: z.boolean().optional(),
        ...clueBaseShape,
    }),
])
    .superRefine((body, ctx) => {
    // The editor hides negativePoints on wager clues; a hidden field is not validation.
    if (body.negativePoints !== undefined && body.scoring.kind === 'wager') {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['negativePoints'],
            message: 'negativePoints cannot be combined with wager scoring (§4.5).',
        });
    }
    if (body.speedBonus?.kind === 'decay' && body.speedBonus.minPoints > body.speedBonus.maxPoints) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['speedBonus', 'minPoints'],
            message: 'A decaying speed bonus cannot have minPoints above maxPoints.',
        });
    }
    if (body.speedBonus?.kind === 'static' &&
        body.speedBonus.scope === 'before' &&
        body.speedBonus.beforeSeconds === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['speedBonus', 'beforeSeconds'],
            message: 'A "before time" speed bonus needs beforeSeconds.',
        });
    }
});
// ---------------------------------------------------------------------------
// The question pool (issue #29)
//
// A reusable, board-agnostic question: no category, position, scoring or tile. It never
// renders on a board — `resolve` skips it — until `questionToClueBody` copies it into a
// `clue`. Tags live on the item row (`content_items.tags`), not the body. Lives in the
// same pack space as clues so the generic `/packs`+`/items` routes serve it unchanged.
// ---------------------------------------------------------------------------
export const MIN_DIFFICULTY = 1;
export const MAX_DIFFICULTY = 5;
/**
 * Normalize a typed answer for comparison: fold diacritics, lowercase, drop apostrophes, turn
 * other punctuation into spaces, collapse whitespace, and drop a leading article. Shared by
 * `autoVerdict` for the correct and accepted answers and the submitted value so "The Café" and
 * "cafe" match. Deliberately moderate — it must not make distinct answers collide.
 */
export function normalizeAnswer(s) {
    const folded = s.normalize('NFKD').replace(/[̀-ͯ]/g, '');
    const stripped = folded
        .toLowerCase()
        .replace(/['’]/g, '')
        .replace(/[^a-z0-9\s]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    return stripped.replace(/^(the|a|an)\s+/, '');
}
export const questionBodySchema = z.object({
    prompt: z.string(),
    correctAnswer: z.string(),
    /** Distractors for a multiple-choice import; empty means the import makes a typed-answer clue. */
    incorrectAnswers: z.array(z.string()).default([]),
    difficulty: z.number().int().min(MIN_DIFFICULTY).max(MAX_DIFFICULTY).default(MIN_DIFFICULTY),
});
/**
 * The answer as a plain number `autoVerdict` can compare ("1,000" → "1000"), or null when it is
 * not one ("Two", "1/3", "16-Jun-04"). Decides whether a hostless game can grade it as a number.
 */
export function numericAnswer(answer) {
    const s = answer.trim().replace(/,(?=\d{3}(\D|$))/g, '');
    return /^-?\d+(\.\d+)?$/.test(s) ? s : null;
}
/**
 * Copy a pool question onto a board as a clue, given where it lands and its face value.
 * Distractors present → multiple choice (choices = correct first, then the distractors);
 * none → a typed-answer clue, or a typed number when auto-grading and the answer is one.
 * Scoring, lockout and timer are the board's defaults, edited afterwards in the clue editor.
 */
export function questionToClueBody(question, placement, opts = {}) {
    const base = {
        categoryId: placement.categoryId,
        prompt: question.prompt,
        position: placement.position,
        scoring: { kind: 'static', points: Math.max(1, placement.points) },
        tileDisplay: { kind: 'points' },
        lockout: opts.lockout ?? 'host_enable',
        answerSeconds: null,
        ...(opts.negativePoints !== undefined ? { negativePoints: opts.negativePoints } : {}),
        ...(opts.speedBonus !== undefined ? { speedBonus: opts.speedBonus } : {}),
    };
    const distractors = question.incorrectAnswers.filter((a) => a.trim() !== '');
    if (distractors.length > 0) {
        let seq = 0;
        const next = () => `ch_${(seq += 1)}`;
        const correct = { id: next(), text: question.correctAnswer };
        const choices = [correct, ...distractors.map((text) => ({ id: next(), text }))];
        return { ...base, questionType: 'multipleChoice', choices, correctChoiceIds: [correct.id] };
    }
    // Play Now wants hostless auto-grading; the board editor leaves text clues host-graded.
    const number = opts.autoGradeText ? numericAnswer(question.correctAnswer) : null;
    if (number !== null)
        return { ...base, questionType: 'numericInput', correctAnswer: number };
    return {
        ...base,
        questionType: 'textInput',
        correctAnswer: question.correctAnswer,
        ...(opts.autoGradeText ? { acceptedAnswers: [] } : {}),
    };
}
/**
 * A round item, stored only when the round is renamed or has a wager pool; otherwise
 * `resolve` falls back to `defaultRoundName`.
 */
export const roundBodySchema = z.object({
    round: z.number().int().positive(),
    name: z.string().min(1).max(MAX_ROUND_NAME).optional(),
    /**
     * Values a `wager` clue draws from, each used once per round
     * (`JeopardyState.wagerAssignments`). On content, not config, because it is edited live
     * and config is pinned at segment start.
     */
    wagerPool: z.object({ values: z.array(z.number().int().positive()).min(1) }).optional(),
});
/**
 * A clue placed on the board but not written yet; the one definition `reduce.ts`, `view.ts`
 * and the host screen share. `trim()` so a lone space still counts as empty.
 */
export function isDraftClue(clue) {
    if (clue.prompt.trim() === '')
        return true;
    if (clue.questionType === 'multipleChoice' || clue.questionType === 'multipleAnswer') {
        return !clue.choices.some((c) => c.text.trim() !== '') || clue.correctChoiceIds.length === 0;
    }
    return clue.correctAnswer.trim() === '';
}
/**
 * Ties sort by id, numerically when both parse as numbers, so `'10'` does not land before
 * `'9'`. Board order must match what hosts already see.
 */
function byId(a, b) {
    const na = Number(a);
    const nb = Number(b);
    if (Number.isFinite(na) && Number.isFinite(nb) && na !== nb)
        return na - nb;
    return a < b ? -1 : a > b ? 1 : 0;
}
/**
 * Carry a v1 clue body forward to the v2 shape. Keyed on shape, so it is safe on any body:
 * a v2 clue or a non-clue (no `mode`) comes back unchanged.
 */
export function migrateClueBody(body) {
    if (typeof body !== 'object' || body === null || Array.isArray(body))
        return body;
    const b = body;
    if ('questionType' in b)
        return body; // already v2
    if (!('mode' in b))
        return body; // not a clue body — leave category/round untouched
    const points = typeof b.points === 'number' ? b.points : 100;
    const questionType = b.mode === 'open' ? 'textInput' : 'verbal';
    const scoring = b.mode === 'daily_double'
        ? { kind: 'accrued', maxWager: points }
        : { kind: 'static', points };
    return {
        categoryId: typeof b.categoryId === 'string' ? b.categoryId : '',
        prompt: typeof b.prompt === 'string' ? b.prompt : '',
        position: typeof b.position === 'number' ? b.position : 0,
        questionType,
        correctAnswer: typeof b.answer === 'string' ? b.answer : '',
        scoring,
        tileDisplay: { kind: 'points' },
        lockout: b.lockout === 'auto_unlock' ? 'auto_unlock' : 'host_enable',
        answerSeconds: typeof b.answerSeconds === 'number' ? b.answerSeconds : null,
    };
}
function resolve(items) {
    const columns = new Map();
    const clues = new Map();
    // Categories first: a clue needs its column to exist before it can be filed.
    const categories = [];
    const names = new Map();
    const pools = new Map();
    for (const item of items) {
        if (item.kind === 'category') {
            const parsed = categoryBodySchema.safeParse(item.body);
            if (parsed.success)
                categories.push({ id: item.id, body: parsed.data, clues: [] });
        }
        else if (item.kind === 'round') {
            const parsed = roundBodySchema.safeParse(item.body);
            if (parsed.success) {
                if (parsed.data.name !== undefined)
                    names.set(parsed.data.round, parsed.data.name);
                if (parsed.data.wagerPool)
                    pools.set(parsed.data.round, parsed.data.wagerPool.values);
            }
        }
    }
    const byCategory = new Map(categories.map((c) => [c.id, c]));
    for (const item of items) {
        if (item.kind !== 'clue')
            continue;
        // Carry a stray v1 body forward rather than drop the clue.
        const parsed = clueBodySchema.safeParse(migrateClueBody(item.body));
        if (!parsed.success)
            continue;
        const clue = { id: item.id, ...parsed.data };
        clues.set(clue.id, clue);
        // A clue whose column was deleted (imported or hand-edited pack) is indexed but
        // lands on no board.
        byCategory.get(clue.categoryId)?.clues.push(clue);
    }
    // Only rounds that hold a category exist.
    const rounds = new Map();
    for (const category of categories) {
        const column = {
            id: category.id,
            category: category.body.name,
            position: category.body.position,
            clues: category.clues.sort((a, b) => a.position - b.position || faceValue(a) - faceValue(b) || byId(a.id, b.id)),
        };
        columns.set(column.id, column);
        const inRound = rounds.get(category.body.round);
        if (inRound)
            inRound.push(column);
        else
            rounds.set(category.body.round, [column]);
    }
    return {
        rounds: [...rounds.entries()]
            .sort(([a], [b]) => a - b)
            .map(([round, cols]) => ({
            round,
            name: names.get(round) ?? defaultRoundName(round),
            columns: cols.sort((a, b) => a.position - b.position || byId(a.id, b.id)),
            ...(pools.has(round) ? { wagerPool: pools.get(round) } : {}),
        })),
        clues,
        columns,
    };
}
export const jeopardyContent = {
    schemas: {
        category: categoryBodySchema,
        clue: clueBodySchema,
        round: roundBodySchema,
    },
    /** A floor against starting a segment on an empty pack, not a board-size rule. */
    minItems: 1,
    resolve,
    /** New items are stamped with this. */
    contentVersion: JEOPARDY_CONTENT_VERSION,
    /**
     * For imported or hand-edited packs; stored bodies are already v2. Kind-agnostic because
     * the hook has no kind argument, and `migrateClueBody` leaves non-clue bodies untouched.
     */
    migrateContent: (_from, body) => migrateClueBody(body),
};
// ---------------------------------------------------------------------------
// Playing a clue: shared by `reduce.ts` and `requests.ts`, which must agree
// ---------------------------------------------------------------------------
/** The static points, or an accrued clue's ceiling. Wager and final clues have no face. */
export function faceValue(clue) {
    switch (clue.scoring.kind) {
        case 'static':
            return clue.scoring.points;
        case 'accrued':
            return clue.scoring.maxWager;
        case 'wager':
            return 0;
        case 'final':
            return 0;
    }
}
/**
 * The three-way mode the client renders by. Every collect type maps to `open`: the phone
 * renders the actual `InputSpec`.
 */
export function legacyClueMode(clue) {
    if (clue.scoring.kind === 'accrued')
        return 'daily_double';
    if (clue.questionType === 'verbal')
        return 'buzzer';
    return 'open';
}
/**
 * True when everyone answers at once rather than racing a buzzer or betting. A `final`
 * clue counts: its wager phase precedes collection.
 */
export function isCollectClue(clue) {
    return (clue.scoring.kind !== 'accrued' &&
        (clue.questionType === 'textInput' ||
            clue.questionType === 'numericInput' ||
            clue.questionType === 'multipleChoice' ||
            clue.questionType === 'multipleAnswer'));
}
/** True for the final round: a per-player wager, then everyone writes. */
export function isFinalClue(clue) {
    return clue.scoring.kind === 'final';
}
/**
 * Whether the rules can settle this clue without a person: the two choice kinds, numeric, and a
 * `textInput` that opted into an accepted-answer list (never a final, which is always host-graded).
 * The one definition `reduce.ts` and `cluePlayStyle` share. A partial `multipleAnswer` still
 * counts — `autoVerdict` returns null for it but `reduce.ts` settles it from the net picks.
 */
export function isAutoGraded(clue) {
    switch (clue.questionType) {
        case 'multipleChoice':
        case 'multipleAnswer':
        case 'numericInput':
            return true;
        case 'textInput':
            return clue.acceptedAnswers !== undefined && clue.scoring.kind !== 'final';
        default:
            return false;
    }
}
export function cluePlayStyle(clue, answerMode) {
    if (clue.scoring.kind === 'accrued')
        return 'wager';
    if (clue.scoring.kind === 'final')
        return 'final';
    if (clue.questionType === 'verbal')
        return 'buzzer';
    return answerMode === 'buzzer' ? 'buzzer' : 'collect';
}
/** Which choice ids were picked; `[]` for text types. Accepts a bare string or an array. */
export function cluePicks(clue, value) {
    if (clue.questionType !== 'multipleChoice' && clue.questionType !== 'multipleAnswer')
        return [];
    if (typeof value === 'string')
        return [value];
    if (Array.isArray(value))
        return value.filter((v) => typeof v === 'string');
    return [];
}
/**
 * A flawless choice pick: every correct option taken and no wrong one. Distinguishes full
 * marks from the partial credit a `multipleAnswer` can still score. `false` for non-choice.
 */
export function isExactPick(clue, value) {
    if (clue.questionType !== 'multipleChoice' && clue.questionType !== 'multipleAnswer')
        return false;
    const picks = new Set(cluePicks(clue, value));
    const want = new Set(clue.correctChoiceIds);
    return picks.size === want.size && [...picks].every((p) => want.has(p));
}
/**
 * Net correct picks for a partial multiple answer: right picks minus wrong ones, floored
 * at zero. A wrong pick cancels a right one, so over-selecting earns less, and picking
 * every option nets nothing. `0` for anything that is not a choice clue.
 */
export function netCorrectPicks(clue, value) {
    if (clue.questionType !== 'multipleChoice' && clue.questionType !== 'multipleAnswer')
        return 0;
    const want = new Set(clue.correctChoiceIds);
    let hits = 0;
    let wrong = 0;
    for (const pick of cluePicks(clue, value)) {
        if (want.has(pick))
            hits += 1;
        else
            wrong += 1;
    }
    return Math.max(0, hits - wrong);
}
/** The clue's *correct* answer as a string for the host and the settled tile — the label
 *  of each correct choice, or the free-text answer. */
export function correctAnswerText(clue) {
    if (clue.questionType === 'multipleChoice' || clue.questionType === 'multipleAnswer') {
        const labels = new Map(clue.choices.map((c) => [c.id, c.text]));
        return clue.correctChoiceIds.map((id) => labels.get(id) ?? id).join(', ');
    }
    return clue.correctAnswer;
}
/** A rendered answer string for the host's grading list and the shared view — the label
 *  of each picked choice, or the typed text. */
export function answerText(clue, value) {
    if (clue.questionType === 'multipleChoice' || clue.questionType === 'multipleAnswer') {
        const labels = new Map(clue.choices.map((c) => [c.id, c.text]));
        return cluePicks(clue, value)
            .map((id) => labels.get(id) ?? id)
            .join(', ');
    }
    return typeof value === 'string' ? value : String(value ?? '');
}
/**
 * The auto-graded verdict, or `null` when a person decides: verbal, a `textInput` without an
 * accepted-answer list, a final, and a partial `multipleAnswer` (whose award is fractional).
 */
export function autoVerdict(clue, value) {
    switch (clue.questionType) {
        case 'numericInput': {
            const got = Number(typeof value === 'string' ? value.trim() : value);
            const want = Number(clue.correctAnswer.trim());
            if (!Number.isFinite(got) || !Number.isFinite(want))
                return 'incorrect';
            const tolerance = clue.tolerance ?? 0;
            return Math.abs(got - want) <= tolerance ? 'correct' : 'incorrect';
        }
        case 'textInput': {
            // Host-graded unless the clue opts in with an accepted-answer list, and never for a final.
            if (clue.acceptedAnswers === undefined || clue.scoring.kind === 'final')
                return null;
            const want = new Set([clue.correctAnswer, ...clue.acceptedAnswers].map(normalizeAnswer).filter((a) => a !== ''));
            const got = normalizeAnswer(typeof value === 'string' ? value : String(value ?? ''));
            return got !== '' && want.has(got) ? 'correct' : 'incorrect';
        }
        case 'multipleChoice': {
            const [pick] = cluePicks(clue, value);
            return pick !== undefined && pick === clue.correctChoiceIds[0] ? 'correct' : 'incorrect';
        }
        case 'multipleAnswer': {
            if (clue.partialScoring)
                return null;
            const picks = new Set(cluePicks(clue, value));
            const want = new Set(clue.correctChoiceIds);
            return picks.size === want.size && [...picks].every((p) => want.has(p))
                ? 'correct'
                : 'incorrect';
        }
        default:
            return null; // verbal (and a host-graded textInput) — a person judges
    }
}
/**
 * An ungraded answer awards nothing. Partial `multipleAnswer` earns
 * `round(hits / totalCorrect * face)`, wrong picks unpenalized. `face` is overridden when
 * the stake is not on the clue (wager pool, final bet).
 */
export function awardFor(clue, verdict, value, face = faceValue(clue)) {
    if (verdict === null)
        return 0;
    if (clue.questionType === 'multipleAnswer' && clue.partialScoring) {
        const want = new Set(clue.correctChoiceIds);
        // Wrong picks cancel right ones, so over-selecting is not a free way to full marks.
        const net = netCorrectPicks(clue, value);
        if (net > 0 && want.size > 0)
            return Math.round((net / want.size) * face);
        return verdict === 'incorrect' && clue.negativePoints !== undefined ? -clue.negativePoints : 0;
    }
    // The final round risks the player's own bet both ways.
    if (clue.scoring.kind === 'final')
        return verdict === 'correct' ? face : -face;
    if (verdict === 'correct')
        return face;
    return clue.negativePoints !== undefined ? -clue.negativePoints : 0;
}
/**
 * The speed bonus each correct answer earns, keyed by participant (absent = none). A pure
 * function of the clue, the reveal time and the answers' verdicts/timestamps, so `reduce.ts`
 * can recompute it before and after a grading pass and settle the difference — a shifted
 * `fastest` bonus follows a corrected ruling to the next-quickest without any stored state.
 * Empty unless the clue is a collect clue carrying a `speedBonus` and has been revealed.
 */
export function speedBonuses(clue, revealedAt, answers) {
    const out = {};
    const bonus = clue.speedBonus;
    if (!bonus || revealedAt === null || !isCollectClue(clue))
        return out;
    const correct = answers.filter((a) => a.verdict === 'correct');
    if (correct.length === 0)
        return out;
    if (bonus.kind === 'static') {
        if (bonus.scope === 'fastest') {
            // Ties on the exact millisecond all share it — rare, and splitting it would be worse.
            const earliest = Math.min(...correct.map((a) => a.submittedAt));
            for (const a of correct)
                if (a.submittedAt === earliest)
                    out[a.participantId] = bonus.points;
        }
        else {
            const cutoff = revealedAt + (bonus.beforeSeconds ?? 0) * 1000;
            for (const a of correct)
                if (a.submittedAt <= cutoff)
                    out[a.participantId] = bonus.points;
        }
        return out;
    }
    // Decay: linear from maxPoints to minPoints across the window, nothing past it.
    const windowMs = bonus.seconds * 1000;
    for (const a of correct) {
        const elapsed = Math.max(0, a.submittedAt - revealedAt);
        if (elapsed >= windowMs)
            continue;
        const fraction = elapsed / windowMs;
        out[a.participantId] = Math.round(bonus.maxPoints - fraction * (bonus.maxPoints - bonus.minPoints));
    }
    return out;
}
//# sourceMappingURL=content.js.map